import { useEffect, useRef, useState } from "react";
import styles from "./questions.module.css";
import { errorCharacters, questions } from "../../constants/questions";
import lottie from 'lottie-web';
import { calculateScore, submitScore } from "../../services/scoreService";
import { ShareButton } from "../share-button/shareButton";
import Keyboard from "react-simple-keyboard";
import 'react-simple-keyboard/build/css/index.css';

const TAUNT_DURATION = 3000;

export default function Questions({ details, onRestart }) {
    const [questionIndex, setQuestionIndex] = useState(0);
    const [selectedLine, setSelectedLine] = useState(0);
    const [answered, setAnswered] = useState(false);
    const [isCorrect, setIsCorrect] = useState(false);
    const [timeLeft, setTimeLeft] = useState(90);
    const [gameOver, setGameOver] = useState(false);
    const [gameStarted, setGameStarted] = useState(false);
    const [countdown, setCountdown] = useState(null);
    const [correctCount, setCorrectCount] = useState(0);
    const [finalTime, setFinalTime] = useState(0);
    const [showError, setShowError] = useState(false);
    const [activeError, setActiveError] = useState(null);
    const [incorrect, setIncorrect] = useState(null);
    const audioRef = useRef(null);
    const { mode, playerName } = details;
    const timeLeftRef = useRef(90);
    const tauntTimeoutRef = useRef(null);
    const secondContainerRef = useRef(null);
    const [firstName, setFirstName] = useState("");
    const [email, setEmail] = useState("");
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [shuffledQuestions] = useState(() => shuffleArray(questions[mode]));
    const [consentAds, setConsentAds] = useState(false);
    const [activeInput, setActiveInput] = useState(null);
    const currentQuestion = shuffledQuestions[questionIndex];

    const keyboardRef = useRef(null);

    const handleKeyboardChange = (input) => {
        if (activeInput === "firstName") setFirstName(input);
        if (activeInput === "email") setEmail(input);
    };

    const getActiveValue = () => {
        if (activeInput === "firstName") return firstName;
        if (activeInput === "email") return email;
        return "";
    };


    useEffect(() => {
        if ((!gameStarted && !gameOver) || !secondContainerRef.current) return;

        const anim = lottie.loadAnimation({
            container: secondContainerRef.current,
            renderer: "svg",
            loop: true,
            autoplay: true,
            path: "/lottie/ants/data.json",
        });

        return () => {
            anim.destroy();
        };
    }, [gameStarted, gameOver]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setStatus(null);

        try {
            const score = calculateScore({
                correctCount,
                totalQuestions: questions[mode].length,
                timeSeconds: finalTime,
            });

            const res = await submitScore({
                firstName,
                email,
                score,
                correctCount,
                totalQuestions: questions[mode].length,
                timeSeconds: finalTime,
                consentAds
            });

            if (!res.success && res.reason === "email_exists") {
                setStatus("This email has already been used 🛑");
                return;
            }

            setStatus("Score submitted! 🎉");
        } catch (err) {
            console.error(err);
            setStatus("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        timeLeftRef.current = timeLeft;
    }, [timeLeft]);

    // 🎮 Wait for Enter to start
    useEffect(() => {
        const handleStart = (e) => {
            if (e.key === "Enter" || e.code === "Space") {
                if (!gameStarted && countdown === null && !gameOver) {
                    startCountdown();
                } else if (gameOver) {
                    // onRestart();
                }
            }
        };
        window.addEventListener("keydown", handleStart);
        return () => window.removeEventListener("keydown", handleStart);
    }, [gameStarted, countdown]);

    // ⏱ Countdown logic
    const startCountdown = () => {
        let count = 3;
        setCountdown(count);
        const interval = setInterval(() => {
            count -= 1;
            if (count === 0) {
                clearInterval(interval);
                setCountdown(null);
                setGameStarted(true);
            } else {
                setCountdown(count);
            }
        }, 1000);
    };

    // 🎯 Game keyboard controls
    useEffect(() => {
        if (!gameStarted || !currentQuestion || answered || gameOver) return;

        const handleKey = (e) => {
            if (answered || gameOver) return;

            if (e.key === "ArrowUp") {
                e.preventDefault();
                setSelectedLine((prev) =>
                    prev > 0 ? prev - 1 : currentQuestion.lines.length - 1
                );
            } else if (e.key === "ArrowDown") {
                e.preventDefault();
                setSelectedLine((prev) => (prev + 1) % currentQuestion.lines.length);
            } else if (e.key === "Enter" || e.code === "Space") {
                handleAnswer(selectedLine);
            }
        };

        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);
    }, [answered, selectedLine, currentQuestion, gameOver, gameStarted]);

    const handleGameOver = () => {
        setGameOver(true);
        setGameStarted(false);

        const timeTaken = 90 - timeLeftRef.current;

        const scoreEntry = {
            playerName,
            correctCount,
            timeTaken,
            date: new Date().toISOString(),
        };

        const existingScores =
            JSON.parse(localStorage.getItem("globalScoreboard")) || [];

        const updatedScores = [...existingScores, scoreEntry].sort((a, b) => {
            if (b.correctCount !== a.correctCount) return b.correctCount - a.correctCount;
            return a.timeTaken - b.timeTaken;
        });

        localStorage.setItem("globalScoreboard", JSON.stringify(updatedScores));
        setFinalTime(timeTaken);
    };

    // 🔁 Restart game
    const restartGame = () => {
        onRestart(true)
    };

    // ⏰ Timer
    useEffect(() => {
        if (!gameStarted || gameOver) return;

        const timer = setInterval(() => {
            setTimeLeft((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    handleGameOver();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [gameOver, gameStarted]);

    function shuffleArray(array) {
        const arr = [...array];
        for (let i = arr.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [arr[i], arr[j]] = [arr[j], arr[i]];
        }
        return arr;
    }

    const handleAnswer = (index) => {
        setAnswered(true);
        const correct = index === currentQuestion.correct;
        setIsCorrect(correct);

        if (correct) {
            setCorrectCount((prev) => prev + 1);
        } else {
            const random =
                errorCharacters[Math.floor(Math.random() * errorCharacters.length)];
            const taunt =
                random.taunts[Math.floor(Math.random() * random.taunts.length)];

            const tauntInstance = { id: Date.now(), ...random, taunt };

            if (tauntTimeoutRef.current) clearTimeout(tauntTimeoutRef.current);

            setActiveError(tauntInstance);
            setShowError(true);
            setIncorrect(index);

            tauntTimeoutRef.current = setTimeout(() => {
                setShowError(false);
                setActiveError(null);
                tauntTimeoutRef.current = null;
            }, TAUNT_DURATION);
        }

        setTimeout(() => {
            if (questionIndex < shuffledQuestions.length - 1) {
                const nextIndex = questionIndex + 1;
                setQuestionIndex(nextIndex);
                setSelectedLine(0);
                setAnswered(false);
                setIsCorrect(false);
                setIncorrect(null);
            } else {
                handleGameOver();
            }
        }, 350);
    };

    if (!currentQuestion) return null; // safety
    return (
        <div className={styles.gameContainer}>

            {activeInput && (
                <Keyboard
                    keyboardRef={(r) => (keyboardRef.current = r)}
                    onChange={handleKeyboardChange}
                    value={getActiveValue()}
                    layout={{
                        default: [
                            "1 2 3 4 5 6 7 8 9 0",
                            "q w e r t y u i o p",
                            "a s d f g h j k l",
                            "{shift} z x c v b n m {bksp}",
                            "@ . _ - {space}"
                        ],
                        shift: [
                            "1 2 3 4 5 6 7 8 9 0",
                            "Q W E R T Y U I O P",
                            "A S D F G H J K L",
                            "{shift} Z X C V B N M {bksp}",
                            "@ . _ - {space}"
                        ]
                    }}
                    display={{
                        "{bksp}": "⌫",
                        "{space}": "␣",
                        "{shift}": "⇧"
                    }}
                />
            )}


            <div className={styles.starsWrap}>
                <div id='stars'></div>
                <div id='stars2'></div>
                <div id='stars3'></div>
            </div>
            {/* Instructions */}
            {!gameStarted && countdown === null && !gameOver && (
                <>
                    <div onClick={() => startCountdown()} className={styles.instructions}>
                        <p className={styles.desktopOnly}>
                            Use the arrow keys ↑↓ to select the buggy line of code.
                        </p>
                        <p className={styles.mobileOnly}>
                            Tap the line of code that contains the bug.
                        </p>
                        <p>You have 90 seconds to solve as many as you can.</p>
                        <p>Press enter to start!</p>
                    </div>
                </>
            )}

            {/* Countdown */}
            {countdown !== null && (
                <div className={styles.countdown}>
                    <h1>{countdown}</h1>
                </div>
            )}

            {/* Game running */}
            {gameStarted && !gameOver && (
                <>
                    <div className={styles.editorWrap}>

                        <div className={styles.starsWrap}>
                            <div id='stars'></div>
                            <div id='stars2'></div>
                            <div id='stars3'></div>
                        </div>

                        <audio ref={audioRef} src="/sound/game-music.mp3" autoPlay loop />
                        <div className={styles.editorHeader}>

                            <div className={styles.buttons}>
                                <span className={styles.red}></span>
                                <span className={styles.yellow}></span>
                                <span className={styles.green}></span>
                            </div>

                            <p className={styles.timeLeft}>{timeLeft}s</p>

                            <div className={styles.filename}>
                                {currentQuestion.filename}
                            </div>
                        </div>

                        <div className={styles.codeArea}>
                            {currentQuestion.lines.map((line, index) => (
                                <div
                                    key={index}
                                    onClick={() => !answered && handleAnswer(index)}
                                    className={`${styles.line} 
                    ${selectedLine === index ? styles.selected : ""} 
                    ${answered && index === currentQuestion.correct ? styles.correctLine : ""} ${index === incorrect ? styles.incorrect : ''}`}
                                >
                                    <span className={styles.lineNumber}>{index + 1}</span>
                                    <code>{line}</code>
                                </div>
                            ))}
                        </div>

                        {answered && (
                            <div
                                className={`${styles.result} ${isCorrect ? styles.success : styles.error
                                    }`}
                            >
                                {isCorrect ? "✅ Correct!" : "❌ Wrong line!"}
                            </div>
                        )}

                        <div className={styles.errors}>
                            {showError && activeError && (
                                <div
                                    key={activeError.id}
                                    className={`${styles.errorCharacter} ${activeError.side === "right" ? styles.fromRight : styles.fromLeft
                                        }`}
                                >
                                    <span className={styles.taunt}>{activeError.taunt}</span>
                                    <img src={activeError.src} alt="" />
                                </div>
                            )}
                        </div>

                    </div>
                </>
            )}

            {/* Game Over */}
            {gameOver && (
                <div className={styles.gameOver}>

                    <audio ref={audioRef} src="/sound/third.mp3" autoPlay loop />

                    <div className={styles.starsWrap}>
                        <div id='stars'></div>
                        <div id='stars2'></div>
                        <div id='stars3'></div>
                    </div>

                    <p className={styles.flash}>
                        <span>
                            GAME OVER
                        </span>
                        <button onClick={onRestart}>↺</button>
                    </p>



                    <div className={styles.scores}>
                        <div>
                            <p>Your score</p>
                            <p>Correct Answers: {correctCount}/{questions[mode].length}</p>
                            <p>Time Taken: {finalTime}s</p>
                        </div>

                        <div>
                            <p>Qodo's score</p>
                            <p>Correct Answers: {questions[mode].length}/{questions[mode].length}</p>
                            <p>Time Taken: 0.8s</p>
                        </div>
                    </div>

                    <div className={styles.trialWrap}>
                        Save time. Use an AI code reviewer <a href="https://www.qodo.ai/get-started/" target="_blank">Try Qodo.</a>
                    </div>

                    <form className={styles.form} onSubmit={handleSubmit}>

                        <p className={styles.gameOverTitle}>
                            Get a chance to win $100
                            <br></br>
                            Choose one option to enter the raffle:
                        </p>

                        <p className={styles.gameOverSubTitle}>
                            Share your score on X
                        </p>

                        <ShareButton correctCount={correctCount} questionsLength={questions[mode].length} finalTime={finalTime}></ShareButton>

                        <p className={styles.divider}>OR</p>
                        <p className={styles.enterRaffle}>
                            Submit your score here (if it’s a perfect score)
                        </p>


                        <div className={styles.fieldWrap}>
                            <div className={styles.field}>
                                <label>Name</label>
                                <input
                                    value={firstName}
                                    onFocus={() => {
                                        setActiveInput("firstName");
                                        keyboardRef?.current?.setInput(firstName);
                                    }}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    required
                                />
                            </div>

                            <div className={styles.field}>
                                <label>Email</label>
                                <input
                                    type="email"
                                    value={email}
                                    onFocus={() => {
                                        setActiveInput("email");
                                        keyboardRef?.current?.setInput(email);
                                    }}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                />
                            </div>
                        </div>

                        <div className="customCheckboxWrapper">
                            <label className="customCheckboxLabel">
                                <input
                                    type="checkbox"
                                    checked={consentAds}
                                    onChange={(e) => setConsentAds(e.target.checked)}
                                />
                                <span className="customCheckbox"></span>
                                Sign me up for marketing updates
                            </label>
                        </div>

                        <div className={styles.submitWrap}>
                            <button className={styles.submit}>
                                {loading ? "SUBMITTING..." : "SUBMIT SCORE"}
                            </button>
                        </div>


                        {status && <p className={styles.status}>{status}</p>}

                        <p className={styles.winnersComment}>*Winners are announced every Monday on Qodo’s X account and notified by email</p>
                    </form>
                </div>
            )}


            <div className={styles.ants} ref={secondContainerRef} />

        </div>
    );
}



